import PrintCloud from "@/classes/PrintCloud";
import { PRINT_CLOUD_STATUS_OPEN } from "@/constants";
import { useMainStore } from "@/store";
import { isRefinementAvailable } from "@/utils/boothRefinements";
import { defineStore } from "pinia";
import { computed, reactive } from 'vue';


export const useCloudStore = defineStore('cloud', () => {
  const mainStore = useMainStore()

  const data = reactive({
    booth_type: null,
    booth: null,
    cloud: new PrintCloud({ uuid: '', status: PRINT_CLOUD_STATUS_OPEN}),
    print_articles: [],
    exhibition_prices: null,
  })

  const baseUrl = computed(() => {
    return '/print/' + data.cloud.uuid
  })

  const basket = computed(() => mainStore.print.basket)

  const boothTypeRefinements = computed(() => {
    if (data.booth === null || data.booth_type === null || data.booth.refinements === null) {
      return []
    }

    return data.booth.refinements.filter((refinement) => {
      return refinement.booth_type === data.booth_type
    })
  })

  const isOpen = computed(() => {
    return data.cloud.status === PRINT_CLOUD_STATUS_OPEN
  })

  const orderedRefinements = computed(() => {
    return data.print_articles.filter((printArticle) => {
      return printArticle.booth_refinement_id !== null
    })
  })

  const basketBoothConfigurations = computed(() => {
    return basket.value.booth_configurations
  })

  const basketPrintConfigurations = computed(() => {
    return basket.value.print_configurations
  })

  const availableRefinements = computed(() => {
    return getAvailableRefinements(
      orderedRefinements.value,
      boothTypeRefinements.value,
      basketBoothConfigurations.value,
      mainStore.availability.disabled_booth_refinement_ids,
      mainStore.availability.bookable_article_ids
    )
  })

  /**
   * 
   * @param {Form} form 
   * @param {Number} configurationId 
   * @returns 
   */
  const updateConfigurationLabel = (form, configurationId) => {
    return postForm(form, '/articles/' + configurationId + '/update-label')
  }

  const changeArticleRefinement = (form, requestArticleId) => {
    return postForm(form, '/articles/' + requestArticleId + '/update-article-refinement')
  }

  const changeBoothRefinement = (form, requestArticleId) => {
    return postForm(form, '/articles/' + requestArticleId + '/update-booth-refinement')
  }

  const changeBoothPrintArticle = (form, requestArticleId) => {
    return postForm(form, '/articles/' + requestArticleId + '/update-booth-print-article')
  }

  const postForm = (form, url) => {
    return new Promise(async (resolve, reject) => {
      await form.post(baseUrl.value + url)
        .then((response) => {
          data.print_articles = response.data.articles
          resolve(true)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  }

  const getAvailableArticleRefinements = (article, configurations, index) => {
    const prints = article.prints
      .filter((print) => mainStore.availability.available_article_ids.includes(print.article_id))
      .filter((print) => mainStore.availability.bookable_article_ids.includes(print.article_id))
    if (configurations.length === 0 && basketPrintConfigurations.value.length === 0) {
      return prints
    }

    const paidArticleIds = configurations.map((configuration) => configuration.request_article_id)
    const basketArticleIds = basketPrintConfigurations.value
      .filter((configuration) => {
        return (
          configuration.group === index
          && configuration.parent_id === article.article_id
        )
      })
      .map((configuration) => configuration.id)

    return prints.filter((item) => {
      if (basketArticleIds.includes(item.id)) {
        return false
      }

      const config = item.configurations
        .filter((configuration) => configuration.group === index)

      if (config.length === 1) {
        return !(paidArticleIds.includes(config[0].request_article_id) || basketArticleIds.includes(config[0].request_article_id))
      }

      return true;
    })
  }

  return {
    data,
    boothTypeRefinements,
    availableRefinements,
    isOpen,
    updateConfigurationLabel,
    changeArticleRefinement,
    changeBoothRefinement,
    changeBoothPrintArticle,
    getAvailableArticleRefinements
  }
})

const getAvailableRefinements = (
  orderedRefinements,
  boothTypeRefinements,
  basketConfigurations,
  disabledBoothRefinementIds,
  bookableArticleIds
) => {
  if (orderedRefinements.length === 0 && basketConfigurations.length === 0) {
    return boothTypeRefinements.filter((refinement) => {
      return isRefinementAvailable(
        refinement,
        disabledBoothRefinementIds,
        bookableArticleIds
      )
    })
  }

  const ids = orderedRefinements.map((refinement) => refinement.booth_refinement_id)
  const basketIds = basketConfigurations
    .map((configuration) => configuration.id)

  return boothTypeRefinements
    .filter((refinement) => {
      return isRefinementAvailable(
        refinement,
        disabledBoothRefinementIds,
        bookableArticleIds
      )
    })
    .filter((refinement) => {
      return (
        (!(ids.includes(refinement.id) || basketIds.includes(refinement.id)))
        && refinement.articles.length > 0
      )
    })
}